import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { Skeleton } from '@/components/skeleton'
import { Carousel } from '@/components/slider'
import { WorkerTypeStamp } from '@/components/worker-type-stamp'
import { useMediaQuery } from '@/hooks/use-media-query'
import { WorkerType } from '@/models/worker'
import api from '@/services/api'

import { DownloadButton } from '../../download-button'
import { ImageOptions, MapCard } from './map-card'
import { MonthEvaluation } from './month-evaluation'

export interface AvailableMonths {
  mes_referencia: string
  id_evidencia: string
}
interface InitialDataResponse {
  id_evidencia_marco_zero: string
  dt_coleta_marco_zero: string
  imagens_marco_zero: {
    imagem_ndvi_inicial: string
    imagem_ndvi_inicial_leve: string
    imagem_rgb_inicial: string
    imagem_rgb_inicial_leve: string
    imagem_rvi_inicial: string
    imagem_rvi_inicial_leve: string
    imagem_sar_inicial: string
    imagem_sar_inicial_leve: string
    imagem_evi_inicial: string
    imagem_evi_inicial_leve: string
    imagem_ndwi_inicial: string
    imagem_ndwi_inicial_leve: string
    imagem_msi_inicial: string
    imagem_msi_inicial_leve: string
    imagem_dpsvi_inicial: string
    imagem_dpsvi_inicial_leve: string
    imagem_dpsvim_inicial: string
    imagem_dpsvim_inicial_leve: string
  }
  mes_referencia_marco_zero: string
  meses_disponiveis: AvailableMonths[] | null
  worker?: WorkerType
}

export interface InitialData {
  evidenceId: string
  date: string
  referenceMonth: string
  imagens: {
    RGB?: ImageOptions
    SAR?: ImageOptions
    NDVI?: ImageOptions
    RVI?: ImageOptions
    EVI?: ImageOptions
    NDWI?: ImageOptions
    MSI?: ImageOptions
    DPSVI?: ImageOptions
    DPSVIM?: ImageOptions
  }
  worker?: WorkerType
}

export function MonitoringTab() {
  const { idProperty } = useParams()
  const isHD = useMediaQuery('(min-width: 1200px)')

  const [initialData, setInitialData] = useState<InitialData>()
  const [availableMonths, setAvailableMonths] = useState<
    AvailableMonths[] | null
  >(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  useEffect(() => {
    async function getData() {
      setIsLoading(true)

      await api
        .get<InitialDataResponse>(
          `/property/monitoring/marco-zero/images/${idProperty}`,
        )
        .then(({ data }) => {
          if (
            data.imagens_marco_zero &&
            Object.keys(data.imagens_marco_zero).length > 0
          ) {
            setInitialData({
              evidenceId: data.id_evidencia_marco_zero,
              date: data.dt_coleta_marco_zero,
              referenceMonth: data.mes_referencia_marco_zero,
              imagens: {
                RGB: {
                  highRes: data.imagens_marco_zero.imagem_rgb_inicial,
                  lowRes: data.imagens_marco_zero.imagem_rgb_inicial_leve,
                },
                SAR: {
                  highRes: data.imagens_marco_zero.imagem_sar_inicial,
                  lowRes: data.imagens_marco_zero.imagem_sar_inicial_leve,
                },
                NDVI: {
                  highRes: data.imagens_marco_zero.imagem_ndvi_inicial,
                  lowRes: data.imagens_marco_zero.imagem_ndvi_inicial_leve,
                },
                RVI: {
                  highRes: data.imagens_marco_zero.imagem_rvi_inicial,
                  lowRes: data.imagens_marco_zero.imagem_rvi_inicial_leve,
                },
                EVI: {
                  highRes: data.imagens_marco_zero.imagem_evi_inicial,
                  lowRes: data.imagens_marco_zero.imagem_evi_inicial_leve,
                },
                NDWI: {
                  highRes: data.imagens_marco_zero.imagem_ndwi_inicial,
                  lowRes: data.imagens_marco_zero.imagem_ndwi_inicial_leve,
                },
                MSI: {
                  highRes: data.imagens_marco_zero.imagem_msi_inicial,
                  lowRes: data.imagens_marco_zero.imagem_msi_inicial_leve,
                },
                DPSVI: {
                  highRes: data.imagens_marco_zero.imagem_dpsvi_inicial,
                  lowRes: data.imagens_marco_zero.imagem_dpsvi_inicial_leve,
                },
                DPSVIM: {
                  highRes: data.imagens_marco_zero.imagem_dpsvim_inicial,
                  lowRes: data.imagens_marco_zero.imagem_dpsvim_inicial_leve,
                },
              },
              worker: data.worker,
            })

            setAvailableMonths(data.meses_disponiveis)
          }
        })
        .catch((error) => {
          toast.error(error.response.data.message)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }

    getData()
  }, [idProperty])

  return (
    <div className="flex flex-col gap-8">
      <p className="text-sm lg:text-lg">
        A 6BIOS realiza a publicação dos índices de preservação das propriedades
        cadastradas e certificadas em sua Plataforma mediante o emprego de
        algoritmos elaborados pelos seus times de ciências e tecnologia
        utilizando dados obtidos com satélites de alta resolução com sensores
        óticos multiespectrais, RADAR de abertura sintética - SAR e LIDAR. As
        imagens aqui apresentadas (RGB - Bandas óticas vermelha, verde e azul,
        NDVI - Normalized Difference Vegetation Index e RVI - RADAR Vegetation
        Index) bem como suas subtrações são reduções destes dados para o simples
        propósito de visualização e documentação. Os índices exatos e as marchas
        de cálculo ficam armazenados nos bancos de dados e estruturas de
        processamento geográfico da 6BIOS, portanto auditáveis e à disposição
        das autoridades reguladoras.
      </p>

      {isLoading ? (
        <>
          <h3 className="text-2xl">Marco zero</h3>

          <div className="grid grid-cols-2 gap-7 sm:grid-cols-4">
            <Skeleton quantity={4} className="aspect-square w-full" />

            <Skeleton className="col-span-2 h-9 w-52 rounded-sm sm:col-span-4" />

            <Skeleton quantity={8} className="aspect-square w-full" />
          </div>
        </>
      ) : initialData ? (
        <>
          <div className="flex justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-2xl">Marco zero</h3>

              <WorkerTypeStamp worker={initialData.worker} />
            </div>

            <DownloadButton
              testId="property-download-monitoring-data-button"
              variant="underline"
              text="Certificado"
              downloadRoute={`/preservation/certificate/monitoring/${initialData.evidenceId}`}
            />
          </div>
          {Object.keys(initialData.imagens).filter(
            (key) =>
              initialData.imagens[key as keyof typeof initialData.imagens]
                ?.highRes ||
              initialData.imagens[key as keyof typeof initialData.imagens]
                ?.lowRes,
          ).length > 4 ? (
            <Carousel
              spaceBetween={30}
              itemsPerPage={isHD ? 4 : 1}
              carouselInsideButtons
              centerItems
            >
              {Object.entries(initialData.imagens).map(([key, image]) => (
                <MapCard
                  key={key}
                  mapData={{
                    data: initialData,
                    title: key,
                    img: image,
                    date: initialData.date,
                    isWithCarousel: true,
                  }}
                />
              ))}
            </Carousel>
          ) : (
            <div className="grid grid-cols-2 gap-7 sm:grid-cols-4">
              {Object.entries(initialData.imagens)
                .filter(([, image]) => image?.highRes || image?.lowRes)
                .map(([key, image]) => (
                  <MapCard
                    key={key}
                    mapData={{
                      data: initialData,
                      title: key,
                      img: image,
                      date: initialData.date,
                      isWithCarousel: true,
                    }}
                  />
                ))}
            </div>
          )}

          {idProperty && (
            <MonthEvaluation
              idProperty={idProperty}
              availableMonths={availableMonths}
            />
          )}
        </>
      ) : (
        <h3 className="text-monochromatic-400 text-center text-base lg:text-start">
          Sem dados de monitoramento
        </h3>
      )}
    </div>
  )
}
